import {defineStore} from 'pinia'
import api from '@/services/api'
import {usePageSectionsStore} from '@/stores/pageSections'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useComponentsStore} from '@/stores/components'
import {useModalsStore} from '@/stores/modals'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'

export const usePageEditorStore = defineStore('pageEditor', {
    state: () => ({
        router: undefined,
        pageId: undefined,
        iframe: undefined,
        
        pageSectionsNode: undefined,
        hoverSectionId: 0,
        activeSectionId: 0,
        onSectionDelete: () => {
        },
        pageSectionTemplates: undefined,
        pageSectionData: undefined,
        
        contentAreas: [],
        hoverContentAreaName: '',
        activeContentAreaName: '',
        
        contentItems: [],
        hoverPageContentId: 0,
        activePageContentId: 0,
        
        pageContainers: undefined,
        
        toolbarDropdown: undefined,
        offcanvas: undefined,
        moveInProgress: false,
        siteTreeId: 'pageEditorSiteTree',
        pinSiteTree: false,
        leftSidebarActiveTab: 'Site tree',
        pagePanelActiveTab: 'Edit',
        contentPanelActiveTab: 'Edit',
        
        showUnpublishedContent: true,
        showPendingContent: true,
        showExpiringContent: true,
        showExpiredContent: true,
        
        offcanvasEnd: false,
        wheelPromise: undefined,
        
        newPage: false,
        editorToolbar: undefined
    }),
    
    getters: {
        isSectionContent: (state) => (pageContentId) => {
            return !!state.contentAreas.find(
                o => o.name === state.contentItems.find(
                    o => o.pageContentId === pageContentId
                )?.contentAreaName
            )?.sectionId
        },
        pageSections: () => {
            return usePageSectionsStore().items || []
        }
    },
    
    actions: {
        setPageId(pageId) {
            this.pageId = pageId
        },
        setIframe(iframe) {
            this.iframe = iframe
        },
        setHoverSectionId(hoverSectionId) {
            this.hoverSectionId = hoverSectionId
        },
        setActiveSectionId(activeSectionId) {
            this.activeSectionId = activeSectionId
        },
        addContentArea(contentArea) {
            contentArea.label = contentArea.sectionId > 0
                ? 'Area ' + (parseInt(contentArea.name.split('_').pop()) + 1)
                : contentArea.name
            
            this.contentAreas.push(contentArea)
        },
        addContentItem(contentItem) {
            contentItem.isHidden = false
            this.contentItems.push(contentItem)
        },
        setHoverContentAreaName(contentAreaName) {
            this.hoverContentAreaName = contentAreaName
        },
        setActiveContentAreaName(contentAreaName) {
            this.activeContentAreaName = contentAreaName
        },
        setHoverPageContentId(pageContentId) {
            this.hoverPageContentId = pageContentId
        },
        setActivePageContentId(pageContentId) {
            this.activePageContentId = pageContentId
        },
        deselectAll() {
            this.hoverContentAreaName = ''
            this.activeContentAreaName = ''
            this.hoverPageContentId = 0
            this.activePageContentId = 0
        },
        setToolbarDropdown(toolbarDropdown) {
            this.toolbarDropdown = toolbarDropdown
        },
        setPinSiteTree(bool) {
            this.pinSiteTree = bool
        },
        toggleShowUnpublishedContent() {
            this.showUnpublishedContent = !this.showUnpublishedContent
        },
        toggleShowPendingContent() {
            this.showPendingContent = !this.showPendingContent
        },
        toggleShowExpiringContent() {
            this.showExpiringContent = !this.showExpiringContent
        },
        toggleShowExpiredContent() {
            this.showExpiredContent = !this.showExpiredContent
        },
        setOffcanvasEnd(bool) {
            this.offcanvasEnd = bool
        },
        setLeftSidebarActiveTab(name) {
            this.leftSidebarActiveTab = name
        },
        
        // --- Store Actions ---
        unset() {
            this.pageSectionsNode = undefined
            
            const pageSectionsStore = usePageSectionsStore()
            pageSectionsStore.items = []
            this.contentAreas = []
            this.contentItems = []
        },
        loadSettings() {
            const settings = JSON.parse(localStorage.getItem('pageEditor'))
            if (settings?.pinSiteTree) {
                this.setPinSiteTree(true)
            }
        },
        async init({
                       router,
                       iframe,
                       sectionId,
                       areaIndex,
                       pageContentId,
                       onSectionDelete
                   }) {
            this.router = router
            this.onSectionDelete = onSectionDelete
            
            this.deselectAll()
            this.setIframe(iframe)
            
            iframe.contentDocument.addEventListener('keydown', (e) => {
                this.keydownEventHandler(e)
            }, {passive: false})
            iframe.contentDocument.addEventListener('keyup', (e) => {
                this.keyupEventHandler(e)
            }, {passive: false})
            
            await this.initAll()
            
            if (pageContentId) {
                this.setActivePageContentId(pageContentId)
                this.setLeftSidebarActiveTab('Content')
            } else if (areaIndex > -1) {
                const contentAreaName = this.contentAreas[areaIndex]?.name
                this.setActiveContentAreaName(contentAreaName)
                this.setLeftSidebarActiveTab('Content')
            } else if (sectionId) {
                this.setActiveSectionId(sectionId)
                this.setLeftSidebarActiveTab('Content')
            }
            
            if (this.showUnpublishedContent) {
                await this.showUnpublishedContentAction()
            }
            
            if (this.newPage) {
                this.newPage = false
                
                const sectionId = await this.addSection()
                await this.goToSection({router, sectionId})
                this.setLeftSidebarActiveTab('Content')
            }
        },
        async initAll() {
            await this.unset()
            
            const body = this.iframe.contentDocument.body
            
            this.initArea({
                el: body.getElementsByTagName('ccms-page-header')[0],
                name: 'Page header',
                sectionId: 0
            })
            
            const pageSectionsNode = body.querySelector('[data-ccms-page-sections]')
            if (pageSectionsNode) {
                this.pageSectionsNode = pageSectionsNode
                
                if (this.pageSectionTemplates === undefined) {
                    this.pageSectionTemplates = (await api.get('api/component/templates_templates', {
                        params: {type: 'section', sort: 'title'}
                    })).data.items
                }
                
                if (this.pageContainers === undefined) {
                    this.pageContainers = (await api.get('api/component/templates_templates', {
                        params: {type: 'container', sort: 'title'}
                    })).data.items
                }
                
                await this.setPageSectionData()
                
                Array.from(pageSectionsNode.children).forEach(section => {
                    this.initSection({el: section})
                })
            }
            
            Array.from(body.querySelectorAll('[data-ccms-content-area]'))
                .forEach(contentArea => {
                    this.initArea({
                        el: contentArea,
                        name: contentArea.dataset.name,
                        sectionId: 0
                    })
                })
            
            this.initArea({
                el: body.getElementsByTagName('ccms-page-footer')[0],
                name: 'Page footer',
                sectionId: 0
            })
        },
        initSection({el, index}) {
            const pageSectionsStore = usePageSectionsStore()
            const sectionId = parseInt(el.id.substring(1))
            const section = {id: sectionId, el}
            
            el.addEventListener('mouseover', () => {
                this.setHoverSectionId(sectionId)
            })
            el.addEventListener('mouseout', () => {
                this.setHoverSectionId(0)
            })
            el.addEventListener('click', () => {
                this.goToSection({router: this.router, sectionId})
            })
            
            if (!pageSectionsStore.items) {
                pageSectionsStore.items = []
            }
            
            index !== undefined
                ? pageSectionsStore.items.splice(index, 0, section)
                : pageSectionsStore.items.push(section)
            
            Array.from(el.querySelectorAll('[data-ccms-section-area]'))
                .forEach(el => this.initArea({el, name: el.dataset.name, sectionId}))
        },
        initArea({el, name, sectionId}) {
            if (el) {
                this.addContentArea({sectionId, el, name})
                
                el.addEventListener('mouseover', (e) => {
                    e.stopPropagation()
                    this.setHoverSectionId(0)
                    this.setHoverContentAreaName(name)
                })
                el.addEventListener('mouseout', () => {
                    this.setHoverContentAreaName('')
                })
                el.addEventListener('click', (e) => {
                    e.stopPropagation()
                    this.goToContentArea({router: this.router, contentAreaName: name})
                })
                
                const content = Array.from(el.querySelectorAll('[data-ccms-editor-content]'))
                content.length
                    ? content.forEach(el => this.initContent({el, contentAreaName: name}))
                    : el.style.minHeight = '50px'
            }
        },
        initContent({el, contentAreaName}) {
            const pageContentId = parseInt(el.id.substring(1))
            
            el.addEventListener('mouseover', (e) => {
                e.stopPropagation()
                this.setHoverSectionId(0)
                this.setHoverContentAreaName('')
                this.setHoverPageContentId(pageContentId)
            })
            el.addEventListener('mouseout', () => {
                this.setHoverPageContentId(0)
            })
            el.addEventListener('click', (e) => {
                e.stopPropagation()
                this.goToContent({router: this.router, pageContentId, pageId: this.pageId})
            })
            
            this.addContentItem({el, pageContentId, contentAreaName})
        },
        async initEditableContent({el, pageContentId}) {
            const pageContentDataStore = usePageContentDataStore()
            const componentsStore = useComponentsStore()
            const componentStructureStore = useComponentStructureStore()
            const itemDataStore = useItemDataStore()
            
            el.style.border = '3px solid transparent'
            
            el.addEventListener('mouseover', (e) => {
                e.stopPropagation()
                el.style.borderColor = 'rgba(9, 110, 253, 0.2)'
                el.style.borderRadius = '5px'
            })
            el.addEventListener('mouseout', () => {
                el.style.borderColor = 'transparent'
                el.style.borderRadius = ''
            })
            
            const contentId = pageContentDataStore.items.find(o => o.id === pageContentId).contentId
            
            const {contentType} = await itemDataStore.get({
                tableName: 'content',
                id: contentId
            })
            
            const tableName = 'component_' + contentType
            const componentId = componentsStore.componentIds[tableName]
            
            const fields = componentStructureStore.get(componentId)
            
            const name = el.dataset.ccmsEditable
            const {type} = fields.find(o => o.columnName === name)
            
            const response = await api.get('api/component/' + tableName, {
                params: {contentId}
            })
            const {id} = response.data.items[0]
            
            const applyEvent = () => {
                el.addEventListener('input', () => {
                    const data = {[name]: el.innerHTML}
                    itemDataStore.update({tableName, id, data})
                })
            }
            
            switch (type) {
                case 'texteditor':
                    el.setAttribute('contenteditable', true)
                    applyEvent()
                    break
                case 'text':
                case 'textarea':
                    el.setAttribute('contenteditable', 'plaintext-only')
                    applyEvent()
                    break
            }
            
            const div = document.createElement('div')
            el.addEventListener('focus', () => {
            })
            el.addEventListener('blur', () => {
                div.remove()
            })
        },
        async setPageSectionData() {
            const response = await api.get('api/page-section/' + this.pageId)
            this.pageSectionData = response.data.data
        },
        async moveSectionUp() {
            const pageSectionsStore = usePageSectionsStore()
            const sections = pageSectionsStore.items || []
            
            if (sections.map(o => o.id).indexOf(this.activeSectionId) === 0)
                return
            
            if (this.moveInProgress) return
            this.moveInProgress = true
            
            await api.patch(`api/page-section/${this.activeSectionId}/up`)
            
            const i = sections.map(o => o.id).indexOf(this.activeSectionId)
            const previousSection = sections[i - 1].el
            const section = sections.splice(i, 1)[0]
            sections.splice(i - 1, 0, section)
            section.el.after(previousSection)
            
            this.moveInProgress = false
        },
        async moveSectionDown() {
            const pageSectionsStore = usePageSectionsStore()
            const sections = pageSectionsStore.items || []
            
            if (sections.map(o => o.id).indexOf(this.activeSectionId) === sections.length - 1)
                return
            
            if (this.moveInProgress) return
            this.moveInProgress = true
            
            await api.patch(`api/page-section/${this.activeSectionId}/down`)
            
            const i = sections.map(o => o.id).indexOf(this.activeSectionId)
            const nextSection = sections[i + 1].el
            const section = sections.splice(i, 1)[0]
            sections.splice(i + 1, 0, section)
            section.el.before(nextSection)
            
            this.moveInProgress = false
        },
        async deleteSection() {
            const pageSectionsStore = usePageSectionsStore()
            const pageContentDataStore = usePageContentDataStore()
            const modalsStore = useModalsStore()
            
            modalsStore.show({
                componentName: 'ConfirmModal',
                obj: {
                    modalTitle: 'Delete section',
                    modalBody: `
                        <p class="alert alert-danger">This <strong>cannot</strong> be undone.</p>
                        <p class="mb-0">Are you sure?</p>
                    `,
                    onConfirm: async () => {
                        await api.delete(`api/page-section/${this.activeSectionId}`)
                        
                        const sections = pageSectionsStore.items || []
                        const i = sections.map(o => o.id).indexOf(this.activeSectionId)
                        const section = sections.splice(i, 1)[0]
                        
                        section.el.remove()
                        
                        this.onSectionDelete()
                        pageContentDataStore.init()
                    },
                },
            })
        },
        async moveUp(pageContentId) {
            const pageContentDataStore = usePageContentDataStore()
            if (this.moveInProgress) return
            this.moveInProgress = true
            
            const pageItems = pageContentDataStore.items.filter(o => o.pageId === this.pageId)
            const item = pageItems.find(o => o.id === pageContentId)
            
            if (item === undefined) return
            
            const contentAreaItems = pageItems.filter(o => o.contentArea === item.contentArea)
            const index = contentAreaItems.map(o => o.id).indexOf(pageContentId)
            const isFirst = index === 0
            
            const content = this.contentItems.find(o => o.pageContentId === pageContentId)
            
            if (isFirst) {
                const index = this.contentAreas.map(o => o.name).indexOf(content.contentAreaName)
                const previousContentArea = this.contentAreas[index - 1]
                
                if (!previousContentArea) {
                    this.moveInProgress = false
                    return
                }
                
                if (previousContentArea.name === 'Page header') {
                    this.moveInProgress = false
                    console.log('Content cannot be moved into the header of legacy page templates.')
                    return
                }
                
                const contentAreaItems = pageItems.filter(o => o.contentArea === previousContentArea.name)
                const newDisplayOrder = contentAreaItems.length + 1
                
                await api.post('api/move-page-content', {
                    pageContentLinkId: pageContentId,
                    pageSectionLinkId: previousContentArea.sectionId,
                    contentArea: previousContentArea.name,
                    displayOrder: newDisplayOrder,
                })
                
                await pageContentDataStore.init()
                
                const parent = content.el.parentNode
                parent.removeChild(content.el)
                
                const contentArea = this.contentAreas.find(o => o.name === previousContentArea.name)
                contentArea.el.append(content.el)
                content.contentAreaName = previousContentArea.name
                
                this.moveInProgress = false
            } else {
                await api.post('api/move-page-content', {
                    pageContentLinkId: pageContentId,
                    pageSectionLinkId: item.pageSectionLinkId,
                    contentArea: item.contentArea,
                    displayOrder: item.displayOrder - 1,
                })
                
                await pageContentDataStore.init()
                
                const previousEl = content.el.previousSibling
                previousEl?.before(content.el)
                
                this.moveInProgress = false
            }
            
            this.flickerContent({el: content.el})
            this.scrollToContent({pageContentId})
        },
        async moveDown(pageContentId) {
            const pageContentDataStore = usePageContentDataStore()
            if (this.moveInProgress) return
            this.moveInProgress = true
            
            const pageItems = pageContentDataStore.items.filter(o => o.pageId === this.pageId)
            const item = pageItems.find(o => o.id === pageContentId)
            
            if (item === undefined) return
            
            const contentAreaItems = pageItems.filter(o => o.contentArea === item.contentArea)
            const index = contentAreaItems.map(o => o.id).indexOf(pageContentId)
            const isLast = index === contentAreaItems.length - 1
            
            const content = this.contentItems.find(o => o.pageContentId === pageContentId)
            
            if (isLast) {
                const index = this.contentAreas.map(o => o.name).indexOf(content.contentAreaName)
                const nextContentArea = this.contentAreas[index + 1]
                
                if (!nextContentArea) {
                    this.moveInProgress = false
                    return
                }
                
                if (nextContentArea.name === 'Page footer') {
                    this.moveInProgress = false
                    console.log('Content cannot be moved into the footer of legacy page templates.')
                    return
                }
                
                await api.post('api/move-page-content', {
                    pageContentLinkId: pageContentId,
                    pageSectionLinkId: nextContentArea.sectionId,
                    contentArea: nextContentArea.name,
                    displayOrder: 1,
                })
                
                await pageContentDataStore.init()
                
                const parent = content.el.parentNode
                parent.removeChild(content.el)
                
                const contentArea = this.contentAreas.find(o => o.name === nextContentArea.name)
                contentArea.el.prepend(content.el)
                content.contentAreaName = nextContentArea.name
                
                this.moveInProgress = false
            } else {
                await api.post('api/move-page-content', {
                    pageContentLinkId: pageContentId,
                    pageSectionLinkId: item.pageSectionLinkId,
                    contentArea: item.contentArea,
                    displayOrder: item.displayOrder + 1,
                })
                
                await pageContentDataStore.init()
                
                const nextEl = content.el.nextSibling
                nextEl?.after(content.el)
                
                this.moveInProgress = false
            }
            
            this.flickerContent({el: content.el})
            this.scrollToContent({pageContentId})
        },
        flickerContent({el}) {
            setTimeout(() => {
                el.style.background = 'rgba(9, 110, 253, 0.2)'
                setTimeout(() => {
                    el.style.background = ''
                }, 200)
            }, 200)
        },
        detachContent(pageContentId) {
            const modalsStore = useModalsStore()
            
            modalsStore.show({
                componentName: 'ConfirmModal',
                obj: {
                    modalTitle: 'Detach content',
                    modalBody: `
                        <p class="alert alert-warning">No other references to this content will be affected.</p>
                    `,
                    onConfirm: async () => {
                        await api.delete(`api/delete-page-content-link-id/${pageContentId}`)
                        this.removeContentFromPage(pageContentId)
                    }
                }
            })
        },
        deleteContent(pageContentId) {
            const pageContentDataStore = usePageContentDataStore()
            const modalsStore = useModalsStore()
            const itemDataStore = useItemDataStore()
            
            const contentId = pageContentDataStore.items.find(o => o.id === pageContentId)?.contentId
            
            const num = pageContentDataStore.items.filter(o => o.contentId === contentId)?.length
            const msg = num === 1
                ? 'This is the <strong>only</strong> instance of this content in use.'
                : '<strong>' + num + '</strong> instances of this content will be removed!'
            
            modalsStore.show({
                componentName: 'ConfirmModal',
                obj: {
                    modalTitle: 'Delete content',
                    modalBody: `
                        <p>` + msg + `</p>
                        <p class="alert alert-danger">This <strong>cannot</strong> be undone.</p>
                        <p class="mb-0">Are you sure?</p>
                    `,
                    onConfirm: async () => {
                        await itemDataStore.delete({
                            tableName: 'content',
                            id: contentId
                        })
                        
                        this.removeContentFromPage(pageContentId)
                    }
                }
            })
        },
        async removeContentFromPage(pageContentId) {
            const pageContentDataStore = usePageContentDataStore()
            
            const content = this.contentItems.find(o => o.pageContentId === pageContentId)
            content.el.remove()
            
            const i = this.contentItems.map(o => o.pageContentId).indexOf(pageContentId)
            this.contentItems.splice(i, 1)
            
            const areaIndex = this.contentAreas.map(o => o.name).indexOf(content.contentAreaName)
            this.router.push({
                name: 'pageEditor',
                params: {pageId: this.pageId, sectionId: this.activeSectionId, areaIndex}
            })
            
            await pageContentDataStore.init()
        },
        async showUnpublishedContentAction() {
            const pageContentDataStore = usePageContentDataStore()
            const itemDataStore = useItemDataStore()
            
            this.contentItems.forEach(obj => {
                const contentId = pageContentDataStore.items.find(o => o.id === obj.pageContentId)?.contentId
                
                itemDataStore.get({tableName: 'content', id: contentId})
                    .then((o) => {
                        if (!this.showUnpublishedContent && o.status === 0) {
                            obj.isHidden = true
                            obj.el.style.setProperty('display', 'none', 'important')
                        } else if (this.showUnpublishedContent) {
                            obj.isHidden = false
                            obj.el.style.removeProperty('display')
                        }
                    })
            })
        },
        scrollToSection({sectionId, behaviour}) {
            const pageSectionsStore = usePageSectionsStore()
            const el = (pageSectionsStore.items || []).find(o => o.id === sectionId)?.el
            this.scrollTo({el, behaviour})
        },
        scrollToContentArea({contentAreaName, behaviour}) {
            const el = this.contentAreas.find(o => o.name === contentAreaName)?.el
            this.scrollTo({el, behaviour})
        },
        scrollToContent({pageContentId, behaviour}) {
            const el = this.contentItems.find(o => o.pageContentId === pageContentId)?.el
            this.scrollTo({el, behaviour})
        },
        scrollTo({el, behaviour}) {
            el.style.scrollMarginTop = '20px'
            
            el.scrollIntoView({
                behavior: behaviour || 'smooth',
                block: 'center',
            })
            
            el.style.scrollMarginTop = ''
        },
        showAddContentModal(contentAreaName) {
            const modalsStore = useModalsStore()
            
            modalsStore.show({
                componentName: 'AddContentModal',
                obj: {
                    onSelect: o => {
                        this.addContentToPage({
                            contentId: o.contentId,
                            contentAreaName
                        })
                    }
                }
            })
        },
        async addContentToPage({contentId, pageSectionLinkId, contentAreaName}) {
            const pageContentDataStore = usePageContentDataStore()
            let o = await api.post('api/save-content-to-page', {
                pageId: this.pageId,
                pageSectionLinkId,
                contentId,
                contentArea: contentAreaName,
            })
            
            const pageContentId = o.data.pageContentId
            
            await pageContentDataStore.init()
            
            await this.placeContentInPage({pageContentId, contentAreaName})
            
            return pageContentId
        },
        async placeContentInPage({pageContentId, contentAreaName}) {
            this.deselectAll()
            
            const {html} = (await api.get('api/page-editor/content/' + pageContentId)).data
            
            const contentEl = new DOMParser().parseFromString(html, 'text/html').body.firstChild
            
            const contentAreaEl = this.contentAreas.find(o => o.name === contentAreaName).el
            contentAreaEl.append(contentEl)
            
            this.initContent({el: contentEl, contentAreaName})
        },
        async reloadContent(pageContentId) {
            const {html} = (await api.get('api/page-editor/content/' + pageContentId)).data
            
            const newContentEl = new DOMParser().parseFromString(html, 'text/html').body.firstChild
            
            const content = this.contentItems.find(o => o.pageContentId === pageContentId)
            content.el.replaceWith(newContentEl)
            content.el = newContentEl
            
            newContentEl.addEventListener('click', (e) => {
                if (this.activePageContentId === pageContentId) {
                    e.stopPropagation()
                }
            })
        },
        async pinSiteTreeAction() {
            this.setPinSiteTree(!this.pinSiteTree)
            await this.setLocalStorage()
        },
        setLocalStorage() {
            localStorage.setItem('pageEditor', JSON.stringify({
                pinSiteTree: this.pinSiteTree,
            }))
        },
        setContentAreaStyling({contentArea, isEmpty}) {
            contentArea.style.minHeight = isEmpty ? '50px' : ''
        },
        keydownEventHandler(e) {
            if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                e.preventDefault()
            }
        },
        keyupEventHandler(e) {
            const pageContentDataStore = usePageContentDataStore()
            if (this.activeSectionId) {
                switch (e.key) {
                    case 'ArrowUp':
                        this.moveSectionUp()
                        return
                    case 'ArrowDown':
                        this.moveSectionDown()
                        return
                    case 'Backspace':
                        this.deleteSection()
                        return
                }
            } else if (this.activePageContentId) {
                switch (e.key) {
                    case 'ArrowUp':
                        this.moveUp(this.activePageContentId)
                        return
                    case 'ArrowDown':
                        this.moveDown(this.activePageContentId)
                        return
                    case 'Backspace':
                        if (e.target.tagName === 'BODY') {
                            const contentId = pageContentDataStore.items.find(o => o.id === this.activePageContentId)?.contentId
                            const num = pageContentDataStore.items.filter(o => o.contentId === contentId)?.length
                            num > 1
                                ? this.detachContent(this.activePageContentId)
                                : this.deleteContent(this.activePageContentId)
                        }
                        return
                }
            }
        },
        async addSection() {
            const {pageSectionLinkId: sectionId, html} = (await api.put(`api/page-section/${this.pageId}`)).data
            
            const section = new DOMParser().parseFromString(html, 'text/html').body.firstChild
            this.pageSectionsNode.append(section)
            
            await this.initSection({el: section})
            await this.setPageSectionData()
            
            this.activeSectionId = sectionId
            
            return sectionId
        },
        async saveSectionTemplateId(sectionTemplateId) {
            const pageSectionsStore = usePageSectionsStore()
            const sectionId = this.activeSectionId
            
            const {html} = (await api.post(`api/page-section/${sectionId}`, {
                sectionTemplateId,
            })).data
            
            const sectionEl = new DOMParser().parseFromString(html, 'text/html').body.firstChild
            
            const sections = pageSectionsStore.items || []
            const section = sections.find(o => o.id === sectionId)
            section.el.replaceWith(sectionEl)
            section.el = sectionEl
            
            this.pageSectionData.find(o => o.id === sectionId).sectionTemplateId = sectionTemplateId
            
            this.initAll()
        },
        async saveSectionContainer(pageContainerId) {
            const pageSectionsStore = usePageSectionsStore()
            const sectionId = this.activeSectionId
            
            const {html} = (await api.post(`api/page-section/${sectionId}`, {
                pageContainerId,
            })).data
            
            const sectionEl = new DOMParser().parseFromString(html, 'text/html').body.firstChild
            
            const sections = pageSectionsStore.items || []
            const section = sections.find(o => o.id === sectionId)
            section.el.replaceWith(sectionEl)
            section.el = sectionEl
            
            this.pageSectionData.find(o => o.id === sectionId).pageContainerId = pageContainerId
            
            this.initAll()
        },
        async goToSection({router, sectionId}) {
            if (this.activeSectionId !== sectionId) {
                router.push({name: 'pageEditor', params: {pageId: this.pageId, sectionId: sectionId}})
            }
        },
        async goToContentArea({router, contentAreaName}) {
            if (this.activeContentAreaName !== contentAreaName) {
                const contentArea = this.contentAreas.find(o => o.name === contentAreaName)
                const sectionId = contentArea.sectionId
                const areaIndex = this.contentAreas.map(o => o.name).indexOf(contentAreaName)
                
                await router.push({name: 'pageEditor', params: {pageId: this.pageId, sectionId, areaIndex}})
            }
        },
        async goToContent({router, pageContentId, pageId}) {
            const pageContentDataStore = usePageContentDataStore()
            this.setLeftSidebarActiveTab('Content')
            
            let sectionId
            let contentAreaName
            const content = this.contentItems.find(o => o.pageContentId === pageContentId)
            if (content) {
                contentAreaName = content.contentAreaName
                sectionId = this.contentAreas.find(o => o.name === contentAreaName)?.sectionId
            } else {
                const pageContentData = pageContentDataStore.items.find(o => o.id === pageContentId)
                contentAreaName = pageContentData.contentArea
                sectionId = pageContentData.pageSectionLinkId
            }
            
            pageId = pageId || this.pageId
            
            if (pageId !== this.pageId || pageContentId !== this.activePageContentId) {
                const areaIndex = this.contentAreas.map(o => o.name).indexOf(contentAreaName)
                await router.push({
                    name: 'pageEditor',
                    params: {pageId, sectionId, areaIndex, pageContentId}
                })
            }
        }
    }
})